import React, { useMemo, useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Icosahedron, Torus } from '@react-three/drei';
import { EffectComposer, Bloom, Noise, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';
import { useTheme } from '../../contexts/ThemeContext';

/* Control-plane core: a glowing Kubernetes-blue wireframe sphere */
function Core() {
  const solid = useRef();
  const wire = useRef();

  useFrame((_, delta) => {
    if (solid.current) {
      solid.current.rotation.y += delta * 0.1;
      solid.current.rotation.x += delta * 0.03;
    }
    if (wire.current) {
      wire.current.rotation.y -= delta * 0.05;
      wire.current.rotation.z += delta * 0.02;
    }
  });

  return (
    <group>
      <Icosahedron ref={solid} args={[1.15, 1]}>
        <meshStandardMaterial
          color="#123056"
          emissive="#326ce5"
          emissiveIntensity={0.55}
          roughness={0.3}
          metalness={0.7}
          flatShading
        />
      </Icosahedron>
      <Icosahedron ref={wire} args={[1.55, 1]}>
        <meshBasicMaterial color="#5b8def" wireframe transparent opacity={0.4} />
      </Icosahedron>
    </group>
  );
}

/* Worker nodes / pods orbiting the control plane */
function Pods({ count = 14 }) {
  const group = useRef();

  const pods = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const radius = 2.6 + (i % 3) * 0.9;
      const angle = (i / count) * Math.PI * 2;
      const tilt = ((i % 4) - 1.5) * 0.4;
      const speed = 0.15 + (i % 5) * 0.03;
      const size = 0.16 + (i % 3) * 0.06;
      const cyan = i % 3 === 0;
      arr.push({ radius, angle, tilt, speed, size, cyan });
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    group.current.children.forEach((mesh, i) => {
      const p = pods[i];
      const a = p.angle + t * p.speed;
      mesh.position.set(
        Math.cos(a) * p.radius,
        Math.sin(a) * p.radius * 0.35 + Math.sin(t + i) * 0.15 + p.tilt,
        Math.sin(a) * p.radius
      );
      mesh.rotation.x += 0.01;
      mesh.rotation.y += 0.012;
    });
  });

  return (
    <group ref={group}>
      {pods.map((p, i) => (
        <mesh key={i}>
          <boxGeometry args={[p.size, p.size, p.size]} />
          <meshStandardMaterial
            color={p.cyan ? '#22d3ee' : '#326ce5'}
            emissive={p.cyan ? '#22d3ee' : '#326ce5'}
            emissiveIntensity={0.7}
            roughness={0.35}
            metalness={0.6}
          />
        </mesh>
      ))}
    </group>
  );
}

/* Orbit rings — the "pipelines" the nodes ride on */
function Orbits({ opacity = 0.22 }) {
  const g = useRef();
  useFrame((_, delta) => {
    if (g.current) g.current.rotation.z += delta * 0.04;
  });
  return (
    <group ref={g} rotation={[Math.PI / 2.4, 0, 0]}>
      {[2.6, 3.5, 4.4].map((r, i) => (
        <Torus key={i} args={[r, 0.004, 8, 128]} rotation={[i * 0.15, 0, 0]}>
          <meshBasicMaterial color="#326ce5" transparent opacity={opacity} />
        </Torus>
      ))}
    </group>
  );
}

/* Drifting data particles */
function Particles({ count = 700, color = '#7fb0ff', opacity = 0.5 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 6 + Math.random() * 14;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.015;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.03} color={color} transparent opacity={opacity} sizeAttenuation depthWrite={false} />
    </points>
  );
}

/* Pointer parallax rig */
function Rig({ children }) {
  const group = useRef();
  useFrame((state) => {
    if (!group.current) return;
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, state.pointer.x * 0.35, 0.04);
    group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, -state.pointer.y * 0.35, 0.04);
  });
  return <group ref={group}>{children}</group>;
}

export default function Scene() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const bg = light ? '#eef1f8' : '#080b11';

  return (
    <div className="scene-bg" aria-hidden="true">
      <Canvas
        dpr={[1, 1.8]}
        camera={{ position: [0, 1, 8], fov: 45 }}
        gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
      >
        <color attach="background" args={[bg]} />
        <fog attach="fog" args={[bg, 9, 24]} />
        <ambientLight intensity={light ? 0.85 : 0.5} />
        <pointLight position={[6, 6, 6]} intensity={2.4} color="#326ce5" />
        <pointLight position={[-8, -3, -4]} intensity={1.6} color="#22d3ee" />
        <pointLight position={[0, 5, -6]} intensity={1.0} color="#5b8def" />

        <Suspense fallback={null}>
          <Rig>
            <Core />
            <Pods />
            <Orbits opacity={light ? 0.32 : 0.22} />
            <Particles color={light ? '#3a6fd8' : '#7fb0ff'} opacity={light ? 0.55 : 0.5} />
          </Rig>
          <EffectComposer disableNormalPass>
            <Bloom
              intensity={light ? 0.55 : 0.85}
              luminanceThreshold={light ? 0.5 : 0.2}
              luminanceSmoothing={0.9}
              mipmapBlur
            />
            <Noise opacity={0.03} />
            <Vignette eskil={false} offset={0.28} darkness={light ? 0.35 : 0.92} />
          </EffectComposer>
        </Suspense>
      </Canvas>
    </div>
  );
}
